import { CONFIG } from '../config.js';
import { blockStride } from '../world/streaming/worldGrid.js';

/** @typedef {'low' | 'medium' | 'high'} DensityTier */

/**
 * Deterministic block-based pedestrian density (global lot grid).
 */
export class NPCPopulation {
  /**
   * @param {typeof CONFIG.city} cityConfig
   * @param {typeof CONFIG.npc.population} populationConfig
   */
  constructor(cityConfig, populationConfig) {
    this.cityConfig = cityConfig;
    this.config = populationConfig;
    this.stride = blockStride(cityConfig);
  }

  /**
   * @param {number} worldX
   * @param {number} worldZ
   * @returns {DensityTier}
   */
  tierAt(worldX, worldZ) {
    const { gx, gz } = this.worldToLotCoord(worldX, worldZ);
    return this.tierAtLot(gx, gz);
  }

  /**
   * @param {number} gx
   * @param {number} gz
   * @returns {DensityTier}
   */
  tierAtLot(gx, gz) {
    const { cityConfig, config } = this;
    const h = this._hashLot(cityConfig.seed, gx, gz);
    const u = (h % 10000) / 10000;
    const { lowCutoff, mediumCutoff } = config;
    if (u < lowCutoff) return 'low';
    if (u < mediumCutoff) return 'medium';
    return 'high';
  }

  /**
   * Relative spawn / destination weight for a nav node.
   * @param {{ x: number; z: number }} node
   */
  weightForNode(node) {
    const tier = this.tierAt(node.x, node.z);
    return this.config.tierWeights[tier];
  }

  /**
   * @param {number} worldX
   * @param {number} worldZ
   */
  worldToLotCoord(worldX, worldZ) {
    const { roadWidth } = this.cityConfig;
    const stride = this.stride;
    const gx = Math.floor((worldX - roadWidth) / stride);
    const gz = Math.floor((worldZ - roadWidth) / stride);
    return { gx, gz };
  }

  _hashLot(seed, gx, gz) {
    let h = seed ^ (gx * 73856093) ^ (gz * 19349663);
    h = Math.imul(h ^ (h >>> 16), 0x7feb352d);
    h = Math.imul(h ^ (h >>> 15), 0x846ca68b);
    return h ^ (h >>> 16);
  }
}
